import { Client } from '@botpress/client'
import * as fs from 'fs'
import * as path from 'path'
import * as process from 'process'
import { auth } from '../auth/index.js'
import { AgentProject } from '../agent-project/agent-project.js'
import { Dependencies } from '../agent-project/types.js'
import { IntegrationManager } from './manager.js'
import { IntegrationRef, IntegrationDefinition } from './types.js'
import { HubCache } from './hub-cache.js'
import { stringifyWithOrder, dependenciesKeyOrder } from '../utils/json-ordering.js'

export interface IntegrationVersionInfo {
  name: string
  version: string
  latestVersion?: string
  availableVersions?: string[]
}

export interface AddIntegrationOptions {
  name: string
  version?: string
  alias?: string
}

export interface AddIntegrationResult {
  success: boolean
  message?: string
  requiresConfiguration?: boolean
  existingVersion?: string
  newVersion?: string
}

export interface UpgradeIntegrationOptions {
  name?: string
  interactive?: boolean
}

export interface RemoveIntegrationOptions {
  name?: string
  interactive?: boolean
}

export class IntegrationOperations {
  private hubCache: HubCache
  private client?: Client
  private projectPath?: string

  constructor(options: { noCache?: boolean; projectPath?: string } = {}) {
    this.hubCache = new HubCache()
    this.projectPath = options.projectPath
  }

  private async getClient(): Promise<Client> {
    if (!this.client) {
      const credentials = await auth.getActiveCredentials()

      let workspaceId = credentials.workspaceId

      try {
        // If projectPath is provided, try to get workspaceId from there
        if (this.projectPath) {
          const project = await AgentProject.load(this.projectPath)
          if (project.agentInfo?.workspaceId) {
            workspaceId = project.agentInfo.workspaceId
          }
        }
      } catch {
        // Ignore errors and fallback to credentials
      }

      if (!workspaceId) {
        throw new Error('No workspace ID found in current profile. Please login again with "adk login"')
      }

      this.client = new Client({
        token: credentials.token,
        apiUrl: credentials.apiUrl,
        workspaceId: workspaceId,
        headers: {
          'x-multiple-integrations': 'true',
        },
      })
    }
    return this.client
  }

  /**
   * Parse integration reference from string (e.g., "slack@latest", "slack@1.0.0")
   */
  private parseIntegrationRef(input: string): IntegrationRef {
    const parts = input.split('@')
    const name = parts[0] || ''
    const version = parts[1] || 'latest'

    if (!name) {
      throw new Error('Invalid integration reference: missing name')
    }

    // Check if it's a workspace integration (contains /)
    const workspaceParts = name.split('/')
    if (workspaceParts.length === 2 && workspaceParts[0] && workspaceParts[1]) {
      return {
        fullName: name,
        workspace: workspaceParts[0],
        name: workspaceParts[1],
        version,
      }
    }

    return { name, version, fullName: name }
  }

  /**
   * Get available versions for an integration
   */
  async getIntegrationVersions(name: string): Promise<IntegrationVersionInfo> {
    const client = await this.getClient()
    const ref = this.parseIntegrationRef(name)

    try {
      // Try to get private/workspace integration first
      const latestResponse = await client.getIntegrationByName({
        name: ref.fullName,
        version: 'latest',
      })

      return {
        name: ref.fullName,
        version: latestResponse.integration.version,
        latestVersion: latestResponse.integration.version,
        availableVersions: [latestResponse.integration.version],
      }
    } catch {
      // Try public integration
      try {
        const latestResponse = await client.getPublicIntegration({
          name: ref.name,
          version: 'latest',
        })

        return {
          name: ref.fullName,
          version: latestResponse.integration.version,
          latestVersion: latestResponse.integration.version,
          availableVersions: [latestResponse.integration.version],
        }
      } catch {
        throw new Error(`Integration "${ref.fullName}" not found`)
      }
    }
  }

  /**
   * Add an integration to dependencies.json
   */
  async addIntegration(options: AddIntegrationOptions): Promise<AddIntegrationResult> {
    const project = await AgentProject.load(this.projectPath || process.cwd())

    const { name, version = 'latest' } = options

    // Check if integration exists and parse reference
    const ref = this.parseIntegrationRef(`${name}@${version}`)

    // Default alias to the base integration name (without workspace prefix)
    // e.g., "sly/intercom" -> alias is "intercom" by default
    const alias = options.alias || ref.name

    // Load current dependencies first to check if integration already exists
    const depsPath = path.join(project.path, 'dependencies.json')
    const dependencies: Dependencies = JSON.parse(fs.readFileSync(depsPath, 'utf-8'))

    if (!dependencies.integrations) {
      dependencies.integrations = {}
    }

    // Check if integration already exists
    const existingIntegration = dependencies.integrations[alias]
    if (existingIntegration) {
      let existingVersion: string
      if (typeof existingIntegration === 'string') {
        const existingRef = this.parseIntegrationRef(existingIntegration)
        existingVersion = existingRef.version
      } else if (typeof existingIntegration === 'object' && existingIntegration.version) {
        const existingRef = this.parseIntegrationRef(existingIntegration.version)
        existingVersion = existingRef.version
      } else {
        existingVersion = 'unknown'
      }

      // If version is not specified or same as existing, do nothing
      if (!version || version === 'latest' || version === existingVersion) {
        return {
          success: false,
          message: `Integration '${alias}' already exists with version ${existingVersion}. Use 'adk upgrade ${alias}' to upgrade to the latest version.`,
          existingVersion,
        }
      }
    }

    // Verify integration exists and get its definition
    let integrationDefinition: IntegrationDefinition
    try {
      const tempManager = new IntegrationManager({
        workspaceId: project.agentInfo?.workspaceId,
      })
      const loadResult = await tempManager.loadIntegrations({
        integrations: {
          [alias]: {
            version: `${ref.fullName}@${ref.version}`,
            enabled: true,
          },
        },
      })

      if (loadResult.errors.length > 0) {
        const firstError = loadResult.errors[0]
        throw new Error(firstError?.message || 'Unknown error')
      }

      // Get the integration definition
      const loadedIntegration = loadResult.integrations[0]
      if (!loadedIntegration?.definition) {
        throw new Error('Failed to load integration definition')
      }
      integrationDefinition = loadedIntegration.definition
    } catch (error) {
      throw new Error(`Failed to add integration: ${error instanceof Error ? error.message : String(error)}`)
    }

    // Check if integration requires configuration
    let requiresConfiguration = false

    // Check modern configurations (plural)
    if (integrationDefinition.configurations) {
      requiresConfiguration = Object.values(integrationDefinition.configurations).some(
        (config: any) => config.identifier?.required === true
      )
    }

    // Check legacy configuration (singular) - if it has required fields in schema
    if (!requiresConfiguration && integrationDefinition.configuration) {
      const config = integrationDefinition.configuration
      const schema = config.schema
      requiresConfiguration = schema?.required && schema.required.length > 0
    }

    // Format the integration entry - use actual version instead of @latest
    const actualVersion = integrationDefinition.version
    const integrationVersion = `${ref.fullName}@${actualVersion}`

    // Preserve existing configuration if it's an object
    const existingEntry = dependencies.integrations[alias]
    if (existingEntry && typeof existingEntry === 'object' && 'version' in existingEntry) {
      // Update only the version, preserving other fields like connection
      dependencies.integrations[alias] = {
        ...existingEntry,
        version: integrationVersion,
      }
    } else {
      // New integration - enable by default if no configuration is required
      dependencies.integrations[alias] = {
        version: integrationVersion,
        enabled: !requiresConfiguration,
      }
    }

    // Write back
    fs.writeFileSync(depsPath, stringifyWithOrder(dependencies, dependenciesKeyOrder) + '\n')

    return {
      success: true,
      requiresConfiguration,
      newVersion: actualVersion,
    }
  }

  /**
   * Remove an integration from dependencies.json
   */
  async removeIntegration(name: string): Promise<void> {
    const project = await AgentProject.load(this.projectPath || process.cwd())

    // Load current dependencies
    const depsPath = path.join(project.path, 'dependencies.json')
    const dependencies: Dependencies = JSON.parse(fs.readFileSync(depsPath, 'utf-8'))

    if (!dependencies.integrations || !dependencies.integrations[name]) {
      throw new Error(`Integration "${name}" not found in dependencies`)
    }

    // Remove integration
    delete dependencies.integrations[name]

    // Write back
    fs.writeFileSync(depsPath, stringifyWithOrder(dependencies, dependenciesKeyOrder) + '\n')
  }

  /**
   * Upgrade integration(s) to latest version
   */
  async upgradeIntegration(name?: string): Promise<{ upgraded: string[]; failed: string[] }> {
    const project = await AgentProject.load(this.projectPath || process.cwd())

    // Load current dependencies
    const depsPath = path.join(project.path, 'dependencies.json')
    const dependencies: Dependencies = JSON.parse(fs.readFileSync(depsPath, 'utf-8'))

    if (!dependencies.integrations) {
      throw new Error('No integrations found in dependencies')
    }

    const upgraded: string[] = []
    const failed: string[] = []

    // Get integrations to upgrade
    const integrationsToUpgrade = name ? { [name]: dependencies.integrations[name] } : dependencies.integrations

    if (name && !integrationsToUpgrade[name]) {
      throw new Error(`Integration "${name}" not found in dependencies`)
    }

    // Upgrade each integration
    for (const [alias, value] of Object.entries(integrationsToUpgrade)) {
      try {
        let ref: IntegrationRef
        let currentVersion: string

        if (typeof value === 'string') {
          ref = this.parseIntegrationRef(value)
          currentVersion = ref.version
        } else if (typeof value === 'object' && value.version) {
          ref = this.parseIntegrationRef(value.version)
          currentVersion = ref.version
        } else {
          failed.push(alias)
          continue
        }

        // Get latest version (using fullName to preserve workspace prefix)
        const versionInfo = await this.getIntegrationVersions(ref.fullName)

        if (versionInfo.latestVersion && versionInfo.latestVersion !== currentVersion) {
          // Update to latest (value is always an object now)
          // Use ref.fullName to preserve workspace prefix (e.g., "sly/intercom")
          dependencies.integrations[alias] = {
            ...value,
            version: `${ref.fullName}@${versionInfo.latestVersion}`,
          }
          upgraded.push(`${alias} (${currentVersion} → ${versionInfo.latestVersion})`)
        }
      } catch {
        failed.push(alias)
      }
    }

    // Write back if any upgrades
    if (upgraded.length > 0) {
      fs.writeFileSync(depsPath, stringifyWithOrder(dependencies, dependenciesKeyOrder) + '\n')
    }

    return { upgraded, failed }
  }

  /**
   * Get list of current integrations
   */
  async listIntegrations(): Promise<Array<{ alias: string; name: string; version: string }>> {
    const project = await AgentProject.load(this.projectPath || process.cwd())

    const depsPath = path.join(project.path, 'dependencies.json')
    const dependencies: Dependencies = JSON.parse(fs.readFileSync(depsPath, 'utf-8'))

    if (!dependencies.integrations) {
      return []
    }

    const integrations: Array<{
      alias: string
      name: string
      version: string
    }> = []

    for (const [alias, value] of Object.entries(dependencies.integrations)) {
      if (typeof value === 'string') {
        const ref = this.parseIntegrationRef(value)
        // Use fullName to preserve workspace prefix (e.g., "sly/intercom")
        integrations.push({ alias, name: ref.fullName, version: ref.version })
      } else if (typeof value === 'object' && value.version) {
        const ref = this.parseIntegrationRef(value.version)
        // Use fullName to preserve workspace prefix (e.g., "sly/intercom")
        integrations.push({ alias, name: ref.fullName, version: ref.version })
      }
    }

    return integrations
  }

  /**
   * Search for integrations in the hub cache
   */
  async searchIntegrations(query: string): Promise<
    Array<{
      name: string
      version: string
      title?: string
      description?: string
    }>
  > {
    const results = await this.hubCache.searchIntegrations(query)

    // Group by name and get latest version for each
    const latestByName = new Map<string, (typeof results)[0]>()
    for (const integration of results) {
      const existing = latestByName.get(integration.name)
      if (
        !existing ||
        integration.version.localeCompare(existing.version, undefined, {
          numeric: true,
        }) > 0
      ) {
        latestByName.set(integration.name, integration)
      }
    }

    return Array.from(latestByName.values()).map((i) => ({
      name: i.name,
      version: i.version,
      title: i.title,
      description: i.description,
    }))
  }

  /**
   * Get all available integrations from hub
   */
  async getAllAvailableIntegrations(): Promise<Array<{ name: string; version: string; title?: string }>> {
    const all = await this.hubCache.getAllIntegrations()

    // Group by name and get latest version for each
    const latestByName = new Map<string, (typeof all)[0]>()
    for (const integration of all) {
      const existing = latestByName.get(integration.name)
      if (
        !existing ||
        integration.version.localeCompare(existing.version, undefined, {
          numeric: true,
        }) > 0
      ) {
        latestByName.set(integration.name, integration)
      }
    }

    return Array.from(latestByName.values()).map((i) => ({
      name: i.name,
      version: i.version,
      title: i.title,
    }))
  }
}
